/**
 * Discount system types and utilities
 */
/**
 * Validate that a discount is within acceptable bounds
 */
export function validateDiscount(discount) {
    const errors = [];
    if (discount.type === 'percentage') {
        if (discount.value < 0 || discount.value > 100) {
            errors.push('Percentage discount must be between 0 and 100');
        }
    }
    if (discount.type === 'fixed') {
        if (discount.value < 0) {
            errors.push('Fixed discount cannot be negative');
        }
    }
    return errors;
}
/**
 * Apply a discount to a base price
 */
export function applyDiscount(basePrice, discount) {
    if (discount.type === 'percentage') {
        return Math.max(0, basePrice * (1 - discount.value / 100));
    }
    else {
        return Math.max(0, basePrice - discount.value);
    }
}
/**
 * Match a pattern against a slug
 * Supports:
 * - Exact match: "vip-gold" matches "vip-gold"
 * - Starts with: "vip-*" matches "vip-gold", "vip-silver"
 * - Ends with: "*-premium" matches "vip-premium", "gold-premium"
 */
export function matchPattern(pattern, slug) {
    // Exact match
    if (!pattern.includes('*')) {
        return pattern === slug;
    }
    // Starts with pattern (e.g., "vip-*")
    if (pattern.endsWith('*')) {
        const prefix = pattern.slice(0, -1);
        return slug.startsWith(prefix);
    }
    // Ends with pattern (e.g., "*-premium")
    if (pattern.startsWith('*')) {
        const suffix = pattern.slice(1);
        return slug.endsWith(suffix);
    }
    return false;
}
/**
 * Parse an eligibility pattern into its components
 */
export function parseEligibilityPattern(pattern) {
    const parts = pattern.split(':');
    if (parts.length !== 2) {
        return null;
    }
    const [type, value] = parts;
    if (type === 'club' ||
        type === 'group' ||
        type === 'punch' ||
        type === 'uid') {
        return { type, value };
    }
    return null;
}
/**
 * Select the best discount from multiple options
 * Rules:
 * 1. Lowest final price wins
 * 2. In case of tie: the buyer's own choice, then club code, then group, then
 *    punch card, then specific
 */
export function selectBestDiscount(discounts) {
    if (discounts.length === 0) {
        return null;
    }
    // Sort by final price (ascending), then by source priority
    // Only breaks ties - the lowest final price still wins outright.
    // An explicit choice wins a tie: what the buyer picked is the more
    // informative thing to record on the ticket and in reports.
    const sourceOrder = {
        selected: 0,
        club: 1,
        group: 2,
        punch: 3,
        specific: 4,
        user: 5,
        none: 6
    };
    discounts.sort((a, b) => {
        // First, compare by final price (lower is better)
        if (a.finalPrice !== b.finalPrice) {
            return a.finalPrice - b.finalPrice;
        }
        // If prices are equal, use source priority (club > punch > specific > user)
        return sourceOrder[a.source] - sourceOrder[b.source];
    });
    return discounts[0];
}
/**
 * Empty discount (no discount applied)
 */
export function emptyAppliedDiscount() {
    return undefined;
}
/**
 * Check if a user is eligible for a specific discount pattern
 */
export function isEligibleForPattern(pattern, context) {
    const parsed = parseEligibilityPattern(pattern);
    if (!parsed) {
        return { eligible: false, source: 'none', sourceId: '' };
    }
    const { type, value } = parsed;
    if (type === 'uid') {
        // Direct user UID match
        if (value === context.uid) {
            return { eligible: true, source: 'user', sourceId: context.uid };
        }
        return { eligible: false, source: 'none', sourceId: '' };
    }
    if (type === 'club') {
        // Check club code slugs
        for (let i = 0; i < context.clubCodeSlugs.length; i++) {
            const slug = context.clubCodeSlugs[i];
            if (matchPattern(value, slug)) {
                return {
                    eligible: true,
                    source: 'club',
                    sourceId: context.clubCodeIds[i] || ''
                };
            }
        }
        return { eligible: false, source: 'none', sourceId: '' };
    }
    if (type === 'group') {
        // Check group membership slugs. The caller has already dropped expired
        // memberships, so anything here is in effect now.
        for (let i = 0; i < context.groupSlugs.length; i++) {
            const slug = context.groupSlugs[i];
            if (matchPattern(value, slug)) {
                return {
                    eligible: true,
                    source: 'group',
                    sourceId: context.groupIds[i] || '',
                    // Names are editable, ids are not - snapshot it so a cart line and a
                    // receipt keep naming the group that earned the discount.
                    label: context.groupNames[i] || ''
                };
            }
        }
        return { eligible: false, source: 'none', sourceId: '' };
    }
    if (type === 'punch') {
        // Check punch card slugs
        for (let i = 0; i < context.punchCardSlugs.length; i++) {
            const slug = context.punchCardSlugs[i];
            if (matchPattern(value, slug)) {
                return {
                    eligible: true,
                    source: 'punch',
                    sourceId: context.punchCardIds[i] || ''
                };
            }
        }
        return { eligible: false, source: 'none', sourceId: '' };
    }
    return { eligible: false, source: 'none', sourceId: '' };
}
/**
 * Check if a user is eligible for a discount config
 * Returns the first matching pattern's details
 */
export function isEligibleForDiscount(discountConfig, context) {
    // If no eligibility patterns, everyone is eligible (specific discount)
    if (!discountConfig.eligibility || discountConfig.eligibility.length === 0) {
        return { eligible: true, source: 'specific', sourceId: 'specific' };
    }
    // Check each eligibility pattern
    for (const pattern of discountConfig.eligibility) {
        const result = isEligibleForPattern(pattern, context);
        if (result.eligible) {
            return result;
        }
    }
    return null;
}
/**
 * Evaluate all available discounts and return possible options
 */
export function evaluateDiscounts(basePrice, discountConfigs, context) {
    const evaluations = [];
    for (const config of discountConfigs) {
        const eligibility = isEligibleForDiscount(config, context);
        if (eligibility && eligibility.eligible) {
            const discount = {
                type: config.type,
                value: config.value
            };
            const finalPrice = applyDiscount(basePrice, discount);
            evaluations.push({
                source: eligibility.source,
                sourceId: eligibility.sourceId,
                discount,
                finalPrice,
                label: eligibility.label
            });
        }
    }
    return evaluations;
}
/**
 * Find the best discount for a given base price and user context
 *
 * A selected price option (student, honnor, ...) competes on equal terms with
 * the discounts the user qualifies for - lowest price wins, no stacking.
 */
export function findBestDiscount(basePrice, discountConfigs, context, selected) {
    const evaluations = evaluateDiscounts(basePrice, discountConfigs, context);
    if (selected) {
        evaluations.push(priceOptionEvaluation(basePrice, selected));
    }
    const best = selectBestDiscount(evaluations);
    if (!best) {
        return undefined;
    }
    const applied = {
        type: best.discount.type,
        value: best.discount.value,
        source: best.source,
        sourceId: best.sourceId
    };
    // Labels are editable, ids are not - snapshot the name so a receipt keeps
    // saying what it said on the day it was issued (as PunchCardDoc does with
    // the whole definition).
    if (best.label) {
        applied.label = best.label;
    }
    return applied;
}
/**
 * How a chosen price option is recorded on a ticket.
 *
 * The same object findBestDiscount produces for a 'selected' evaluation, built
 * straight from the catalog so the server can describe a choice without having
 * to go through the pricer - which cannot price an option on a slot that is
 * already free. The label is snapshotted here for the reason given on
 * AppliedDiscount.label: ids are stable, labels get edited.
 */
export function toAppliedPriceOption(option) {
    return {
        type: option.discount.type,
        value: option.discount.value,
        source: 'selected',
        sourceId: option.id,
        label: option.label
    };
}
/**
 * Evaluate a price option the user has chosen for themselves
 */
export function priceOptionEvaluation(basePrice, option) {
    return {
        source: 'selected',
        sourceId: option.id,
        label: option.label,
        discount: option.discount,
        finalPrice: applyDiscount(basePrice, option.discount)
    };
}
/**
 * Parse a raw price option catalog (site config)
 */
export function toPriceOptions(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw
        .filter((item) => item && typeof item === 'object')
        .map((item) => {
        const type = item.discount?.type === 'fixed' ? 'fixed' : 'percentage';
        return {
            id: typeof item.id === 'string' ? item.id : '',
            label: typeof item.label === 'string' ? item.label : '',
            discount: {
                type,
                value: toDiscountValue(item.discount?.value, type)
            },
            note: typeof item.note === 'string' ? item.note : '',
            requireConfirm: item.requireConfirm === true
        };
    })
        .filter((option) => option.id !== '');
}
const toDiscountValue = (raw, type) => {
    const value = typeof raw === 'string' ? Number(raw) : raw;
    if (typeof value !== 'number' || isNaN(value) || value < 0)
        return 0;
    // A percentage over 100 would price nothing below zero (applyDiscount
    // clamps), but it reads as a configuration mistake rather than an intent -
    // hold it at "free" so the editor and the price shown agree
    if (type === 'percentage' && value > 100)
        return 100;
    return value;
};
/**
 * Resolve which of the site's price options a definition offers.
 *
 * `spec` is the comma separated id list stored on SlotDef.priceOptions and
 * EventSub.price.options. Globbing works as everywhere else ('*' for all,
 * 'stud*', '*-pris'). Ids that no longer resolve are dropped, so removing an
 * option from the catalog withdraws it everywhere - it fails closed.
 */
export function resolvePriceOptions(catalog, spec) {
    if (!spec || !catalog || catalog.length === 0)
        return [];
    const patterns = spec
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '');
    if (patterns.length === 0)
        return [];
    return catalog.filter((option) => patterns.some((pattern) => matchPattern(pattern, option.id)));
}
/**
 * Look up one option a user claims, among those a definition offers.
 * Returns undefined when the claim does not resolve - callers must then fall
 * back to the ordinary price rather than trusting the claim.
 */
export function findOfferedPriceOption(catalog, spec, optionId) {
    if (!optionId)
        return undefined;
    return resolvePriceOptions(catalog, spec).find((option) => option.id === optionId);
}
/**
 * Build a discount user context from user shadow doc and cart
 * Note: This requires importing types from cart and user, which should be done by the caller
 * to avoid circular dependencies. This is a utility that can be used in the pricing logic.
 */
export function buildDiscountContext(params) {
    return {
        uid: params.uid,
        clubCodeSlugs: params.clubCodes.map((c) => c.slug),
        clubCodeIds: params.clubCodes.map((c) => c.id),
        punchCardSlugs: params.punchCards.map((p) => p.slug),
        punchCardIds: params.punchCards.map((p) => p.id),
        groupSlugs: (params.groups ?? []).map((g) => g.slug),
        groupIds: (params.groups ?? []).map((g) => g.id),
        groupNames: (params.groups ?? []).map((g) => g.name ?? '')
    };
}
//# sourceMappingURL=discount.js.map